import { useEffect, useState } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  redirect,
  Route,
  RouterProvider,
} from "react-router-dom";

import {
  PublicReferralRewardLayout,
  PublicReferralRewardLayoutLoader,
} from "@/layouts";
import { MobileOnlyLayout } from "@/layouts/MobileOnlyLayout";
import {
  PublicGiftCardLayout,
  publicGiftCardLayoutLoader,
} from "@/layouts/PublicGiftCardLayout";
import NextSearchPage, { nextSearchLoader } from "@/pages/NextSearchPage";
import ClaimGiftCardPage, {
  claimGiftCardLoader,
} from "@/pages/root/ClaimGiftCardPage";
import PurchaseGiftCardPageCart, {
  giftCardCartLoader,
} from "@/pages/root/gift-cards/GiftCardCartPage";
import PurchaseGiftCardPageCheckout, {
  purchaseGiftCardCheckoutLoader,
} from "@/pages/root/gift-cards/GiftCardCheckoutPage";
import PurchaseGiftCardPage, {
  purchaseGiftCardLoader,
} from "@/pages/root/gift-cards/GiftCardPurchasePage";
import PurchaseGiftCardPageSuccess, {
  giftCardSuccessLoader,
} from "@/pages/root/gift-cards/GiftCardSuccessPage";
import {
  ReturnsLoader,
  ReturnsPage,
  SelectedItemReturnFlow,
  SelectedItemReturnLoader,
} from "@/pages/root/return";
import ClaimReferralRewardPage, {
  claimReferralRewardLoader,
} from "@/pages/root/rewards/ClaimReferralRewardPage";
import ReferralRewardsPage, {
  referralRewardsPageLoader,
} from "@/pages/root/rewards/ReferralRewardsPage";
import BookingProviders, {
  bookingProvidersLoader,
} from "@/pages/root/shop/Booking/BookingProviders";
import GiftCardDetailsPage, {
  giftCardDetailsLoader,
} from "@/pages/root/wallet/GiftCards/GiftCardDetailsPage";
import {
  repeatCashActivityDetailsLoader,
  RepeatCashActivityDetailsPage,
} from "@/pages/root/wallet/RepeatCash/RepeatCashDetailsPage/RepeatCashActivityDetailsPage";
import Services from "@/services/Services";
import { PersonalisationRoutes } from "modules/personalisation/routes/PersonalisationRoutes";
import Root, { rootLoader } from "pages/Root";
import {
  accountDetailsAction,
  accountDetailsLoader,
  AccountDetailsPage,
} from "pages/root/account/account-details/AccountDetailsPage";
import { DeleteMyAccountPage } from "pages/root/account/account-details/delete-my-account";
import AccountPage, { accountLoader } from "pages/root/account/AccountPage";
import {
  orderHistoryDetailsAction,
  orderHistoryDetailsLoader,
  OrderHistoryDetailsPage,
} from "pages/root/account/order-history/OrderHistoryDetailsPage";
import { OrderHistoryPage } from "pages/root/account/order-history/OrderHistoryPage";
import { RewardHistoryPage } from "pages/root/account/RewardHistoryPage";
import { VisitHistoryPage } from "pages/root/account/VisitHistoryPage";
import AnonymousLayout, { anonymousLoader } from "pages/root/AnonymousLayout";
import AppLayout, {
  AppLayoutError,
  appLayoutLoader,
} from "pages/root/AppLayout";
import AppLayoutNoBottomNav, {
  AppLayoutErrorNoBottomNav,
} from "pages/root/AppLayoutNoBottomNav";
import AuthenticatedOnboardingLayout, {
  AuthenticatedOnboardingLayoutError,
  authenticatedOnboardingLoader,
} from "pages/root/AuthenticatedOnboardingLayout";
import CartPage, { cartLoader, cartPageAction } from "pages/root/cart/CartPage";
import CheckoutElementPage, {
  checkoutElementLoader,
} from "pages/root/cart/CheckoutElementPage";
import {
  checkoutRewardTrackerLoader,
  CheckoutRewardTrackerPage,
} from "pages/root/cart/CheckoutRewardTrackerPage";
import CheckoutSuccessPage, {
  CheckoutSuccessPageLoader,
} from "pages/root/cart/CheckoutSuccessPage";
import MembershipRewardsSelectionPage, {
  MembershipRewardsSelectionPageLoader,
} from "pages/root/cart/MembershipRewardsSelectionPage";
import {
  changePlatformLoader,
  ChangePlatformPage,
} from "pages/root/ChangePlatformPage";
import CloseBrowserPaymentUpdatedPage from "pages/root/CloseBrowserPaymentUpdatedPage";
import { Deactivated, deactivatedLoader } from "pages/root/Deactivated";
import DiscoverPage, { discoverLoader } from "pages/root/DiscoverPage";
import { FaqsLoader, FaqsPage } from "pages/root/faq";
import BookingPage, {
  glpBookingLoader,
} from "pages/root/health/glp/pages/BookingPage";
import GLPConfirmation, {
  glpConfirmationLoader,
} from "pages/root/health/glp/pages/GLPConfirmation";
import GlpQuestionnaire, {
  questionnaireLoader,
} from "pages/root/health/glp/pages/Questionnaire";
import HomePage, { homeLoader } from "pages/root/home/HomePage";
import RewardsIndex, {
  rewardsIndexLoader,
} from "pages/root/rewards/RewardsIndex";
import RewardsPage, {
  rewardsAction,
  rewardsLoader,
} from "pages/root/rewards/RewardsPage";
import ScanCheckInPage from "pages/root/ScanCheckInPage";
import ScanPickupRetailProductsItem from "pages/root/ScanPickupRetailProductsItem";
import ScanRedeemOfferInLocationPage, {
  ScanRedeemOfferInLocationLoader,
} from "pages/root/ScanRedeemOfferInLocationPage";
import ScanRedeemRetailProductsOrder from "pages/root/ScanRedeemRetailProductsOrder";
import ScanRedeemRewardInLocationPage from "pages/root/ScanRedeemRewardInLocationPage";
import MembershipPDP from "pages/root/shop/MembershipPDP";
import { membershipPDPLoader } from "pages/root/shop/MembershipPDP/MembershipPDP";
import PackagePDP from "pages/root/shop/PackagePDP";
import { packagePDPLoader } from "pages/root/shop/PackagePDP/PackagePDP";
import ProductPDP, {
  productPDPLoader,
} from "pages/root/shop/ProductPDP/ProductPDP";
import PublicShopPage, {
  publicShopLoader,
} from "pages/root/shop/public/PublicShopPage";
import ShopBrowseByConcernPage, {
  shopBrowseByConcernsLoader,
} from "pages/root/shop/ShopBrowseByConcernPage";
import ShopByConcernPage, {
  shopByConcernLoader,
} from "pages/root/shop/ShopByConcernPage";
import ShopCustomCategoryPage, {
  shopCustomCategoryLoader,
} from "pages/root/shop/ShopCustomCategory";
import ShopGiftCardsPage, {
  shopGiftCardsLoader,
} from "pages/root/shop/ShopGiftCardsPage";
import ShopIndex, { shopIndexLoader } from "pages/root/shop/ShopIndex";
import ShopMembershipsPage, {
  shopMembershipsLoader,
} from "pages/root/shop/ShopMembershipsPage";
import ShopPackagesPage, {
  shopPackagesLoader,
} from "pages/root/shop/ShopPackagesPage";
import ShopProductsListPage, {
  shopProductsListLoader,
} from "pages/root/shop/ShopProductsListPage";
import ShopProductsPage, {
  shopProductsLoader,
} from "pages/root/shop/ShopProductsPage";
import ShopRecommendedTreatmentsPage from "pages/root/shop/ShopRecommendedTreatmentsPage";
import ShopTreatmentsPage, {
  shopTreatmentsLoader,
} from "pages/root/shop/ShopTreatmentsPage";
import ShopWeightLossProgramsPage, {
  shopWeightLossProgramsLoader,
} from "pages/root/shop/ShopWeightLossProgramsPage";
import WeightLossProgramPDP from "pages/root/shop/WeightLossProgramPDP";
import WeightLossProgramCheckout, {
  weightLossProgramCheckoutLoader,
} from "pages/root/shop/WeightLossProgramPDP/WeightLossProgramCheckout";
import WeightLossProgramCheckoutSuccessPage, {
  weightLossProgramCheckoutSuccessLoader,
} from "pages/root/shop/WeightLossProgramPDP/WeightLossProgramCheckoutSuccessPage";
import { weightLossProgramPDPLoader } from "pages/root/shop/WeightLossProgramPDP/WeightLossProgramPDP";
import ShopPage from "pages/root/ShopPage";
import {
  accountCreatedLoader,
  AccountCreatedPage,
} from "pages/root/signIn/AccountCreatedPage";
import { adminImpersonateLoader } from "pages/root/signIn/AdminImpersonationPage";
import {
  friendReferralLandingLoader,
  FriendReferralLandingPage,
} from "pages/root/signIn/FriendReferralLandingPage";
import { preRegisteredUserLoginLoader } from "pages/root/signIn/PreRegisteredUserLoginPage";
import {
  completeProfileLoader,
  CompleteProfilePage,
} from "pages/root/signIn/signup/CompleteProfilePage";
import {
  findMedspaLoader,
  FindMedspaPage,
} from "pages/root/signIn/signup/find-medspa";
import {
  searchLocationLoader,
  SearchLocationPage,
} from "pages/root/signIn/signup/find-medspa/SearchLocationPage";
import {
  signUpFriendReferralLoader,
  SignUpFriendReferralPage,
} from "pages/root/signIn/SignUpFriendReferralPage";
import V3DateOfBirthPage from "pages/root/signIn/V3DateOfBirthPage";
import V3EmailPage from "pages/root/signIn/V3EmailPage";
import V3GenderPage from "pages/root/signIn/V3GenderPage";
import V3GiftCardRedeemPage from "pages/root/signIn/V3GiftCardRedeemPage";
import V3LocationPicker, {
  v3LocationPickerLoader,
} from "pages/root/signIn/V3LocationPicker";
import V3NamePage from "pages/root/signIn/V3NamePage";
import V3NotificationsPage from "pages/root/signIn/V3NotificationsPage";
import V3PlatformPicker, {
  v3PlatformPickerLoader,
} from "pages/root/signIn/V3PlatformPicker";
import V3RewardsPage from "pages/root/signIn/V3RewardsPage";
import V3ShareFriendsPage, {
  v3ShareFriendsLoader,
  V3ShareFriendsPage_URL,
} from "pages/root/signIn/V3ShareFriendsPage";
import V3SignInPage, {
  v3SignInDisplayLoader,
} from "pages/root/signIn/V3SignInPage";
import V3SignupSourcePage from "pages/root/signIn/V3SignupSourcePage";
import V3VerificationCodePage from "pages/root/signIn/V3VerificationCodePage";
import {
  WalletGiftCardsPage,
  walletGiftCardsPageLoader,
} from "pages/root/wallet/GiftCards/WalletGiftCardsPage";
import {
  walletMembershipManageLoader,
  WalletMembershipsManagePage,
} from "pages/root/wallet/Memberships/WalletManageMembershipsPage";
import {
  walletMembershipCancelAction,
  walletMembershipCancelConfirmLoader,
  WalletMembershipCancelConfirmPage,
} from "pages/root/wallet/Memberships/WalletMembershipCancelConfirmPage";
import {
  walletMembershipCancelLoader,
  WalletMembershipCancelPage,
} from "pages/root/wallet/Memberships/WalletMembershipCancelPage";
import {
  walletMembershipDunningCancelAction,
  WalletMembershipDunningCancelConfirmPage,
} from "pages/root/wallet/Memberships/WalletMembershipDunningCancelConfirmPage";
import {
  walletMembershipDunningCancelLoader,
  WalletMembershipDunningCancelPage,
} from "pages/root/wallet/Memberships/WalletMembershipDunningCancelPage";
import WalletMembershipPage, {
  walletMembershipAction,
  walletMembershipLoader,
} from "pages/root/wallet/Memberships/WalletMembershipPage";
import WalletEarnedProductDetailsPage, {
  walletEarnedProductDetailsLoader,
} from "pages/root/wallet/Products/WalletEarnedProductDetailsPage";
import WalletProductOrderDetailsPage, {
  walletProductOrderDetailsLoader,
} from "pages/root/wallet/Products/WalletProductOrderDetailsPage";
import WalletProductsPage, {
  walletProductsLoader,
} from "pages/root/wallet/Products/WalletProductsPage";
import {
  WalletRepeatCashPage,
  walletRepeatCashPageLoader,
} from "pages/root/wallet/RepeatCash/WalletRepeatCashPage";
import WalletTreatmentsPage, {
  walletTreatmentsLoader,
} from "pages/root/wallet/Treatments";
import ScanRedeemTreatmentPage from "pages/root/wallet/Treatments/ScanRedeemTreatmentPage";
import {
  treatmentLoader,
  TreatmentPage,
} from "pages/root/wallet/Treatments/TreatmentPage";
import WalletLayout, {
  walletLayoutLoader,
} from "pages/root/wallet/WalletLayout";
import WalletOfferPage, {
  walletOfferLoader,
} from "pages/root/wallet/WalletOfferPage";
import WalletOffersPage, {
  walletOffersLoader,
} from "pages/root/wallet/WalletOffersPage";
import RootError from "pages/RootError";
import SearchPage from "pages/SearchPage";

const PublicOnboardingRoutes = (
  <Route path="/signin">
    <Route
      index
      element={<V3SignInPage />}
      handle={{ name: "Sign In" }}
      loader={v3SignInDisplayLoader}
    />
    <Route
      path="/signin/verification"
      element={<V3VerificationCodePage />}
      handle={{ name: "Sign In" }}
    />
    <Route
      path="/signin/platform-picker"
      element={<V3PlatformPicker />}
      loader={v3PlatformPickerLoader}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/location"
      element={<V3LocationPicker />}
      loader={v3LocationPickerLoader}
      handle={{ name: "Sign Up" }}
    />
  </Route>
);

const AuthenticatedOnboardingRoutes = (
  <Route path="/signin">
    <Route
      path="/signin/name"
      element={<V3NamePage />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/dob"
      element={<V3DateOfBirthPage />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/location"
      element={<V3LocationPicker />}
      loader={v3LocationPickerLoader}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/email"
      element={<V3EmailPage />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/source"
      element={<V3SignupSourcePage />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/gender"
      element={<V3GenderPage />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/gift-card-redeem"
      element={<V3GiftCardRedeemPage />}
      handle={{ name: "Gift Card Claim" }}
    />
    <Route
      path="/signin/rewards"
      element={<V3RewardsPage />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path={V3ShareFriendsPage_URL}
      loader={v3ShareFriendsLoader}
      element={<V3ShareFriendsPage />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/notifications"
      element={<V3NotificationsPage />}
      handle={{ name: "Sign Up" }}
    />
  </Route>
);

const publicReferralRewardRoutes = (
  <Route
    element={<PublicReferralRewardLayout />}
    loader={PublicReferralRewardLayoutLoader}
    path="/referral-rewards"
  >
    <Route
      path="/referral-rewards/:referralCode"
      element={<ClaimReferralRewardPage />}
      loader={claimReferralRewardLoader}
      handle={{ name: "Referral Reward" }}
    />
  </Route>
);

const publicGiftCardRoutes = (
  <Route
    element={<PublicGiftCardLayout />}
    loader={publicGiftCardLayoutLoader}
    path="/gift-cards"
  >
    <Route
      path="/gift-cards/claim"
      element={<ClaimGiftCardPage />}
      loader={claimGiftCardLoader}
    />
    <Route
      path="/gift-cards/purchase"
      element={<PurchaseGiftCardPage />}
      loader={purchaseGiftCardLoader}
    />

    <Route
      path="/gift-cards/cart"
      element={<PurchaseGiftCardPageCart />}
      loader={giftCardCartLoader}
    />

    <Route
      path="/gift-cards/checkout"
      element={<PurchaseGiftCardPageCheckout />}
      loader={purchaseGiftCardCheckoutLoader}
    />

    <Route
      path="/gift-cards/success"
      element={<PurchaseGiftCardPageSuccess />}
      loader={giftCardSuccessLoader}
    />
  </Route>
);

const anonymousRoutes = (
  <Route element={<MobileOnlyLayout />}>
    <Route element={<AnonymousLayout />} loader={anonymousLoader}>
      {PublicOnboardingRoutes}

      <Route
        // This page route should be kept in sync with the admin app.
        // The admin app hardcodes a link to this page in
        // Shared/RepeatMD.Shared/Commands/Admin/Users/RequestMagicLinkForPlatformUserInPatientAppCommand.cs
        path="/admin-impersonate"
        loader={adminImpersonateLoader}
        handle={{ name: "Admin Impersonate" }}
      />
      <Route
        // This page route should be kept in sync with the admin app.
        // The admin app hardcodes a link to this page in
        // Shared/RepeatMD.Shared/Commands/Admin/Users/CreateCustomPlanCommand.cs
        path="/pre-registered-user-login"
        loader={preRegisteredUserLoginLoader}
        handle={{ name: "Login" }}
      />
      <Route
        path="/referral/:referralCode"
        element={<FriendReferralLandingPage />}
        loader={friendReferralLandingLoader}
        handle={{ name: "Sign In Referral" }}
      />
      {/* <Route
          path="/referral-rewards/:referralCode"
          element={<ReferralLandingPage />}
          loader={referralLandingLoader}
          handle={{ name: "Sign In Referral" }}
        /> */}
      <Route
        path="/v1migration"
        loader={() => redirect("/signin")}
        handle={{ name: "Sign In" }}
      />
      <Route
        path="/stripe-landing/close-browser"
        element={<CloseBrowserPaymentUpdatedPage />}
      />
      <Route
        path="/shop/public"
        loader={publicShopLoader}
        element={<PublicShopPage />}
      />
    </Route>
  </Route>
);

const authenticatedRoutes = (
  <Route element={<MobileOnlyLayout />}>
    <Route
      element={<AppLayout />}
      loader={appLayoutLoader}
      errorElement={<AppLayoutError />}
    >
      <Route
        path="/home"
        loader={homeLoader}
        element={<HomePage />}
        handle={{ name: "Home" }}
      />
      <Route
        path="/discover"
        element={<DiscoverPage />}
        loader={discoverLoader}
        handle={{ name: "Discover" }}
      />
      <Route path="/shop" element={<ShopPage />}>
        <Route
          index
          loader={shopIndexLoader}
          element={<ShopIndex />}
          handle={{ name: "Shop" }}
        />
        <Route
          path="/shop/recommended-treatments"
          element={<ShopRecommendedTreatmentsPage />}
          handle={{ name: "Recommended for you" }}
        />
        <Route
          path="/shop/memberships"
          loader={shopMembershipsLoader}
          element={<ShopMembershipsPage />}
          handle={{ name: "Shop Memberships" }}
        />
        <Route
          path="/shop/treatments"
          loader={shopTreatmentsLoader}
          element={<ShopTreatmentsPage />}
          handle={{ name: "Shop Treatments" }}
        />
        <Route
          path="/shop/products"
          loader={shopProductsLoader}
          element={<ShopProductsPage />}
          handle={{ name: "Shop Products" }}
        />
        <Route
          path="/shop/packages"
          loader={shopPackagesLoader}
          element={<ShopPackagesPage />}
          handle={{ name: "Shop Packages" }}
        />
        <Route
          path="/shop/concerns"
          loader={shopBrowseByConcernsLoader}
          element={<ShopBrowseByConcernPage />}
          handle={{ name: "Shop Browse by Concern" }}
        />
        <Route
          path="/shop/weight-loss-programs"
          loader={shopWeightLossProgramsLoader}
          element={<ShopWeightLossProgramsPage />}
          handle={{ name: "Shop Weight Loss Programs" }}
        />
        <Route
          path="/shop/gift-cards"
          element={<ShopGiftCardsPage />}
          loader={shopGiftCardsLoader}
          handle={{ name: "Shop Gift Cards" }}
        />
        <Route
          path="/shop/categories/:id"
          element={<ShopPackagesPage />}
          handle={{ name: "Shop Categories" }}
        />
        <Route
          path="/shop/custom-category/:id"
          loader={shopCustomCategoryLoader}
          element={<ShopCustomCategoryPage />}
        />
      </Route>
      <Route
        path="/products/list"
        loader={shopProductsListLoader}
        element={<ShopProductsListPage />}
        handle={{ name: "Shop Products List" }}
      />
      <Route
        path="/shop/concern/:id"
        loader={shopByConcernLoader}
        element={<ShopByConcernPage />}
        handle={{ name: "Shop By Concern" }}
      />
      <Route
        path="/pdp/membership/:id"
        loader={membershipPDPLoader}
        element={<MembershipPDP />}
      />
      <Route
        path="/pdp/membership/:id/:source"
        loader={membershipPDPLoader}
        element={<MembershipPDP />}
      />
      <Route
        path="/pdp/product/:id"
        loader={productPDPLoader}
        element={<ProductPDP />}
      />
      <Route
        path="/pdp/product/:id/:source"
        loader={productPDPLoader}
        element={<ProductPDP />}
      />
      <Route
        path="/pdp/package/:id"
        loader={packagePDPLoader}
        element={<PackagePDP />}
      />
      <Route
        path="/booking/:id/providers"
        element={<BookingProviders />}
        loader={bookingProvidersLoader}
      />
      <Route
        path="/pdp/weight-loss-program/:id"
        loader={weightLossProgramPDPLoader}
        element={<WeightLossProgramPDP />}
      />
      <Route
        path="/pdp/weight-loss-program/checkout/:id"
        loader={weightLossProgramCheckoutLoader}
        element={<WeightLossProgramCheckout />}
      />
      <Route
        path="/shop/weight-loss-program/checkout/:id/success"
        loader={weightLossProgramCheckoutSuccessLoader}
        element={<WeightLossProgramCheckoutSuccessPage />}
      />
      <Route path="/rewards">
        <Route
          index
          loader={rewardsIndexLoader}
          element={<RewardsIndex />}
          handle={{ name: "Rewards" }}
        />
        <Route
          path="/rewards/:rewardId"
          action={rewardsAction}
          loader={rewardsLoader}
          element={<RewardsPage />}
          handle={{ name: "Reward Details" }}
        />
        {/* <Route
            path="/rewards/track-referrals"
            element={<TrackReferralsPage />}
            loader={rewardsLoader}
            handle={{ name: "Track Referrals" }}
          /> */}
        <Route
          path="/rewards/referrals"
          element={<ReferralRewardsPage />}
          loader={referralRewardsPageLoader}
        />
      </Route>

      <Route path="/wallet">
        <Route index loader={() => redirect("/wallet/treatments")} />
        <Route loader={walletLayoutLoader} element={<WalletLayout />}>
          <Route
            path="/wallet/treatments"
            loader={walletTreatmentsLoader}
            element={<WalletTreatmentsPage />}
            handle={{ name: "Wallet Treatments" }}
          />
          <Route
            path="/wallet/offers"
            loader={walletOffersLoader}
            element={<WalletOffersPage />}
            handle={{ name: "Wallet Offers" }}
          />
          <Route
            path="/wallet/products"
            loader={walletProductsLoader}
            element={<WalletProductsPage />}
            handle={{ name: "Wallet Products" }}
          />
          <Route
            path="/wallet/memberships"
            loader={walletMembershipLoader}
            action={walletMembershipAction}
            element={<WalletMembershipPage />}
            handle={{ name: "Wallet Memberships" }}
          />
          <Route
            path="/wallet/repeatcash"
            loader={walletRepeatCashPageLoader}
            element={<WalletRepeatCashPage />}
            handle={{ name: "Wallet RepeatCash" }}
          />
          <Route
            path="/wallet/giftcards"
            loader={walletGiftCardsPageLoader}
            element={<WalletGiftCardsPage />}
            handle={{ name: "Wallet Gift Cards" }}
          />
          <Route
            path="/wallet/giftcards/:giftCardTransferId"
            element={<GiftCardDetailsPage />}
            loader={giftCardDetailsLoader}
            handle={{ name: "Gift Card Details" }}
          />
        </Route>
        <Route
          path="/wallet/offers/:offerId"
          element={<WalletOfferPage />}
          loader={walletOfferLoader}
          handle={{ name: "Wallet Offer Details" }}
        />
        <Route
          path="/wallet/treatments/:packageId"
          element={<TreatmentPage />}
          loader={treatmentLoader}
          handle={{ name: "Treatment Details" }}
        />
        <Route
          path="/wallet/products/order/:orderId"
          loader={walletProductOrderDetailsLoader}
          element={<WalletProductOrderDetailsPage />}
          handle={{ name: "Order Details" }}
        />
        <Route
          path="/wallet/products/earned/:packageId"
          loader={walletEarnedProductDetailsLoader}
          element={<WalletEarnedProductDetailsPage />}
          handle={{ name: "Earned Product Details" }}
        />
        <Route
          path="/wallet/memberships/manage"
          loader={walletMembershipManageLoader}
          element={<WalletMembershipsManagePage />}
          handle={{ name: "Manage memberships" }}
        />
        <Route
          path="/wallet/memberships/manage/:membershipId"
          loader={walletMembershipCancelLoader}
          element={<WalletMembershipCancelPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:membershipId/dunning"
          loader={walletMembershipDunningCancelLoader}
          element={<WalletMembershipDunningCancelPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:membershipId/dunning/cancel"
          loader={walletMembershipDunningCancelLoader}
          action={walletMembershipDunningCancelAction}
          element={<WalletMembershipDunningCancelConfirmPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:membershipId/cancel"
          loader={walletMembershipCancelConfirmLoader}
          action={walletMembershipCancelAction}
          element={<WalletMembershipCancelConfirmPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        {/* Handle redirects from old /my-treatments page */}
        <Route
          path="/wallet/my-treatments"
          loader={() => redirect("/wallet/treatments")}
        />
        <Route
          path="/wallet/my-treatments/*"
          loader={({ params }) => {
            const path = params["*"];
            return redirect(`/wallet/treatments/${path}`);
          }}
        />
        {/* Handle redirects from old /membership page */}
        <Route
          path="/wallet/membership"
          loader={() => redirect("/wallet/memberships")}
        />
        <Route
          path="/wallet/membership/*"
          loader={({ params }) => {
            const path = params["*"];
            return redirect(`/wallet/memberships/${path}`);
          }}
        />
      </Route>

      <Route path="/account">
        <Route index loader={accountLoader} element={<AccountPage />} />
        <Route
          path="/account/details"
          action={accountDetailsAction}
          loader={accountDetailsLoader}
          element={<AccountDetailsPage />}
          handle={{ name: "Account Details" }}
        />
        <Route
          path="/account/delete-my-account"
          element={<DeleteMyAccountPage />}
          handle={{ name: "Delete My Account" }}
        />
        <Route path="/account/visits">
          <Route
            index
            element={<VisitHistoryPage />}
            handle={{ name: "Visit History" }}
          />
        </Route>
        <Route path="/account/orders">
          <Route
            index
            element={<OrderHistoryPage />}
            handle={{ name: "Order History" }}
          />
        </Route>
        <Route path="/account/orders/:shopDisplayOrderId">
          <Route
            index
            loader={orderHistoryDetailsLoader}
            action={orderHistoryDetailsAction}
            element={<OrderHistoryDetailsPage />}
            handle={{ name: "Order History Details" }}
          />
        </Route>
        <Route
          path="/account/reward-history"
          element={<RewardHistoryPage />}
          handle={{ name: "Reward History" }}
        />

        {/* Handle redirects from old /account/wallet page */}
        <Route
          path="/account/wallet"
          loader={() => redirect("/wallet/repeatcash")}
        />
        <Route path="/account/wallet/repeatcash/:transactionId">
          <Route
            index
            loader={repeatCashActivityDetailsLoader}
            element={<RepeatCashActivityDetailsPage />}
          />
        </Route>

        {/* Redirect /account/settings/* to /account/* */}
        <Route path="/account/settings" loader={() => redirect("/account")} />
        <Route
          path="/account/settings/*"
          loader={({ params }) => {
            const path = params["*"];
            return redirect(`/account/${path}`);
          }}
        />

        {/* Catch-all redirect for anything else under /account/* to /wallet/* */}
        <Route
          path="/account/*"
          loader={({ params }) => {
            const path = params["*"];
            return redirect(`/wallet/${path}`);
          }}
        />
      </Route>
      <Route path="/search" element={<SearchPage />} />
      <Route
        path="/search-services"
        element={<NextSearchPage />}
        loader={nextSearchLoader}
      />
      <Route
        path="/faqs"
        loader={FaqsLoader}
        element={<FaqsPage />}
        handle={{ name: "FAQS" }}
      />
    </Route>
  </Route>
);

const noAuthCheckRoutes = (
  <Route element={<MobileOnlyLayout />}>
    <Route index loader={() => redirect("/home")} />
    <Route
      path="/app/*"
      loader={(request) => {
        const path = request.params["*"];
        return redirect(`/${path}`);
      }}
    />
    <Route
      path="/deactivated"
      element={<Deactivated />}
      loader={deactivatedLoader}
    />
    <Route
      path="/change-location"
      loader={changePlatformLoader}
      element={<ChangePlatformPage />}
    />
    <Route
      path="/signin/location"
      element={<V3LocationPicker />}
      loader={v3LocationPickerLoader}
      handle={{ name: "Sign Up" }}
    />
    <Route path="/signin/signup/find-medspa">
      <Route
        index
        element={<FindMedspaPage />}
        loader={findMedspaLoader}
        handle={{ name: "Sign Up" }}
      />
      <Route
        path="/signin/signup/find-medspa/search-location"
        element={<SearchLocationPage />}
        loader={searchLocationLoader}
        handle={{ name: "Sign Up" }}
      />
    </Route>
    <Route
      path="/signin/signup/complete-profile"
      element={<CompleteProfilePage />}
      handle={{ name: "Sign Up" }}
      loader={completeProfileLoader}
    />
    <Route
      path="/stripe-landing"
      loader={() =>
        redirect(`/wallet/memberships/manage${window.location.search}`)
      }
    />
    <Route
      path="/signin/account-created"
      element={<AccountCreatedPage />}
      handle={{ name: "Account Created" }}
      loader={accountCreatedLoader}
    />
    <Route
      path="/signin/friend-referral"
      element={<SignUpFriendReferralPage />}
      handle={{ name: "Share Reward" }}
      loader={signUpFriendReferralLoader}
    />
  </Route>
);

const authenticatedRoutesNoBottomNav = (
  <Route element={<MobileOnlyLayout />}>
    <Route
      element={<AppLayoutNoBottomNav />}
      loader={appLayoutLoader}
      errorElement={<AppLayoutErrorNoBottomNav />}
    >
      <Route
        path="/custom-plan/:id"
        loader={packagePDPLoader}
        element={<PackagePDP />}
      />
      <Route
        path="/discoverNoBottomNav"
        element={<DiscoverPage />}
        loader={discoverLoader}
        handle={{ name: "Discover" }}
      />
      <Route path="/cart">
        <Route
          index
          action={cartPageAction}
          loader={cartLoader}
          element={<CartPage />}
          handle={{ name: "Your Shopping Cart" }}
        />
        <Route
          path="/cart/checkout/element"
          loader={checkoutElementLoader}
          element={<CheckoutElementPage />}
          handle={{ name: "Checkout Element" }}
        />
        <Route
          path="/cart/checkout/success"
          element={<CheckoutSuccessPage />}
          handle={{ name: "Checkout Success" }}
          loader={CheckoutSuccessPageLoader}
        />
        <Route
          path="/cart/checkout/membership-rewards"
          element={<MembershipRewardsSelectionPage />}
          handle={{ name: "Membership Rewards Selection" }}
          loader={MembershipRewardsSelectionPageLoader}
        />
        <Route
          path="/cart/checkout/reward"
          element={<CheckoutRewardTrackerPage />}
          handle={{ name: "Checkout Reward Tracker" }}
          loader={checkoutRewardTrackerLoader}
        />
      </Route>
      <Route
        path="/health/glp/questionnaire"
        loader={questionnaireLoader}
        element={<GlpQuestionnaire />}
      />
      <Route
        path="/health/glp/booking"
        element={<BookingPage />}
        loader={glpBookingLoader}
      />
      <Route
        path="/health/glp/confirmation"
        element={<GLPConfirmation />}
        loader={glpConfirmationLoader}
      />
      <Route
        path="/products/order/:id/return"
        loader={ReturnsLoader}
        element={<ReturnsPage />}
      />
      <Route
        path="/products/order/:id/return/:itemId"
        loader={SelectedItemReturnLoader}
        element={<SelectedItemReturnFlow />}
      />
      {/* Scan routes without footer */}
      <Route path="/scan-checkin-page" element={<ScanCheckInPage />} />
      <Route
        path="/scan-redeem-offer-in-location"
        element={<ScanRedeemOfferInLocationPage />}
        loader={ScanRedeemOfferInLocationLoader}
      />
      <Route
        path="/scan-redeem-reward-in-location"
        element={<ScanRedeemRewardInLocationPage />}
      />
      <Route
        path="/scan-redeem-treatment-in-location"
        element={<ScanRedeemTreatmentPage />}
      />
      <Route
        path="/scan-redeem-retail-products-order"
        element={<ScanRedeemRetailProductsOrder />}
      />
      <Route
        path="/scan-pickup-retail-products-item"
        element={<ScanPickupRetailProductsItem />}
      />
    </Route>
  </Route>
);

const authenticatedOnboardingRoutes = (
  <Route element={<MobileOnlyLayout />}>
    <Route
      element={<AuthenticatedOnboardingLayout />}
      loader={authenticatedOnboardingLoader}
      errorElement={<AuthenticatedOnboardingLayoutError />}
    >
      {AuthenticatedOnboardingRoutes}
    </Route>
  </Route>
);
export function AppRouter() {
  const [router, setRouter] = useState<ReturnType<
    typeof createBrowserRouter
  > | null>(null);

  useEffect(() => {
    const initRouter = async () => {
      await Services.auth.loadToken();
      const newRouter = createBrowserRouter(
        createRoutesFromElements(
          <Route
            path="/"
            loader={rootLoader}
            element={<Root />}
            errorElement={<RootError />}
          >
            {noAuthCheckRoutes}
            {anonymousRoutes}
            {authenticatedRoutes}
            {authenticatedRoutesNoBottomNav}
            {authenticatedOnboardingRoutes}
            {publicGiftCardRoutes}
            {publicReferralRewardRoutes}
            {PersonalisationRoutes}
          </Route>
        )
      );
      setRouter(newRouter);
    };

    initRouter();
  }, []);

  if (!router) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-color border-b-transparent" />
      </div>
    );
  }

  return <RouterProvider router={router} />;
}
